import React from 'react';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import Client from './../Client';
import BackofficeReportsApi from './backofficeReportsApi';

function thousandsSeparator(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function formatChange(value) {
  const num = parseFloat(value || 0);

  if (num > 0) return `+${num.toFixed(1)}%`;
  if (num < 0) return `${num.toFixed(1)}%`;

  return `0.0%`;
}

class OverviewRegisteredOrders extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client("none"),
      data: {},
      filterData: {}
    };
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.periodMode !== this.props.periodMode
      || prevProps.dateFrom !== this.props.dateFrom
      || prevProps.dateTo !== this.props.dateTo
      || prevProps.location !== this.props.location
    ) {
      this.loadData();
    }
  }

  loadData = () => {
    const queryData = {
      ...(this.props.periodMode === 'custom' ? { dateFrom: this.props.dateFrom, dateTo: this.props.dateTo } : {}),
      periodMode: this.props.periodMode || "last_30d",
      location: this.props.location || "0",
    };

    const data = BackofficeReportsApi.getClientsRegisteredAnonymousSummary(this.state.client, queryData);

    if (data) {
      this.setState({
        data: data,
        filterData: queryData,
      }, () => {
        if (this.props.onReportMeta) {
          this.props.onReportMeta(
            data.period,
            data.generated_at || data.generatedAt || new Date().toISOString(),
          );
        }
      });
    } else {
      this.setState({
        data: {},
        filterData: queryData,
      });
    }
  };

  render() {
    let registeredOrders = 0;
    let totalOrders = 0;
    let orderShare = 0;
    let changePct = 0;
    const livePeriod = this.props.periodMode === 'today';
    let periodLabel = livePeriod
      ? 'Today (in progress)'
      : this.props.periodMode === 'custom' ? 'Selected period'
        : this.props.periodMode === 'yesterday' ? 'Yesterday' : 'Last 30 complete days';

    const current = this.state.data?.current || {};
    const change = this.state.data?.change || {};
    const period = this.state.data?.period || {};

    if (current.registered_orders !== undefined) {
      registeredOrders = parseInt(current.registered_orders || 0);
    }

    if (current.total_orders !== undefined) {
      totalOrders = parseInt(current.total_orders || 0);
    }

    if (current.registered_order_share !== undefined) {
      orderShare = parseFloat(current.registered_order_share || 0);
    }

    if (change.registered_orders_pct !== undefined) {
      changePct = parseFloat(change.registered_orders_pct || 0);
    }

    if (period.label && this.props.periodMode !== 'custom') {
      periodLabel = period.label;
    }
    const displayValue =
      registeredOrders > 1000
        ? thousandsSeparator((registeredOrders / 1000).toFixed(1)) + "K"
        : thousandsSeparator(registeredOrders.toFixed(0));

    const changeColor =
      livePeriod
        ? '#64748b'
        : changePct > 0
        ? '#047857'
        : changePct < 0
          ? '#b91c1c'
          : '#64748b';

    const changeBg =
      livePeriod
        ? 'rgba(100, 116, 139, 0.08)'
        : changePct > 0
        ? 'rgba(4, 120, 87, 0.08)'
        : changePct < 0
          ? 'rgba(185, 28, 28, 0.08)'
          : 'rgba(100, 116, 139, 0.08)';

    return (
      <Card
        sx={{
          overflow: 'hidden',
          position: 'relative',
          borderRadius: { xs: '14px', sm: '16px' },
          height: '100%',
          width: '100%',
          boxSizing: 'border-box',
          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
          background: 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)',
          border: '1px solid rgba(15, 23, 42, 0.06)',
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: '#1a237e',
          },
        }}
      >
        <CardContent
          sx={{
            height: '100%',
            boxSizing: 'border-box',
            p: {
              xs: 1.5,
              sm: 1.75,
              md: 2,
            },
            '&:last-child': {
              pb: {
                xs: 1.5,
                sm: 1.75,
                md: 2,
              },
            },
          }}
        >
          <Stack
            sx={{
              height: '100%',
              minHeight: {
                xs: 104,
                sm: 118,
              },
            }}
            justifyContent="space-between"
            spacing={1.25}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                spacing={1}
                sx={{ minWidth: 0 }}
              >
                <Typography
                  sx={{
                    lineHeight: 1.2,
                    color: '#1a237e',
                    fontWeight: 800,
                    letterSpacing: '.015em',
                    fontSize: {
                      xs: '0.72rem',
                      sm: '0.78rem',
                      md: '0.84rem',
                    },
                    textTransform: 'uppercase',
                    minWidth: 0,
                  }}
                >
                  Registered orders
                </Typography>

                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 0.65,
                    py: 0.22,
                    borderRadius: 999,
                    bgcolor: changeBg,
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    sx={{
                      color: changeColor,
                      fontWeight: 800,
                      fontSize: {
                        xs: '0.66rem',
                        sm: '0.72rem',
                      },
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {livePeriod ? 'LIVE' : formatChange(changePct)}
                  </Typography>
                </Box>
              </Stack>

              <Typography
                sx={{
                  mt: 0.6,
                  fontWeight: 800,
                  lineHeight: 1,
                  color: '#111827',
                  fontSize: {
                    xs: '2rem',
                    sm: '2.2rem',
                    md: '2.35rem',
                  },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayValue}
              </Typography>

              <Typography
                color="text.secondary"
                sx={{
                  mt: 0.5,
                  fontSize: {
                    xs: '0.68rem',
                    sm: '0.74rem',
                  },
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {livePeriod ? periodLabel : `${periodLabel} vs previous period`}
              </Typography>
            </Box>

            <Stack
              alignItems="center"
              direction="row"
              spacing={0.75}
              sx={{ minWidth: 0 }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.35,
                  px: 0.7,
                  py: 0.25,
                  borderRadius: 999,
                  bgcolor: 'rgba(26, 35, 126, 0.08)',
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    color: '#1a237e',
                    fontWeight: 800,
                    fontSize: {
                      xs: '0.82rem',
                      sm: '0.88rem',
                    },
                    lineHeight: 1.2,
                  }}
                >
                  {orderShare.toFixed(1)}%
                </Typography>
              </Box>

              <Typography
                color="text.secondary"
                sx={{
                  fontSize: {
                    xs: '0.74rem',
                    sm: '0.8rem',
                  },
                  lineHeight: 1.25,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: 0,
                }}
              >
                of {thousandsSeparator(totalOrders.toFixed(0))} orders
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  }
}

export default OverviewRegisteredOrders;
